import { create } from 'zustand';

/**
 * Outlook iframe bearer token store.
 *
 * Holds the pane's access_token and refresh_handle. The refresh_token never
 * reaches the browser — it stays in BFF/Redis and is referenced by the opaque
 * refresh_handle.
 *
 * This is intentionally separate from `useAuthStore` (which manages the main
 * React app's cookie-session profile). The Outlook iframe is a scoped
 * CLAUDE.md exception.
 *
 * Source contracts:
 *  - `access_token`, `refresh_handle`, `expires_in`, `tenant_id` come from
 *    POST /bff/auth/dialog-exchange or POST /bff/auth/refresh.
 *  - `expiresAt` is computed in the consumer as `Date.now() + expires_in*1000`.
 *
 * Persistence (localStorage):
 *  - On `setTokens`, the tokens are written to `localStorage[STORAGE_KEY]`.
 *  - On `clear`, the entry is removed.
 *  - On module load, the entry is read and rehydrated:
 *      • If the stored `expiresAt` is still fresh, accessToken + refreshHandle
 *        + tenantId are all restored.
 *      • If the stored `expiresAt` is stale BUT the entry is younger than the
 *        7-day persistence cap, ONLY refreshHandle + tenantId are restored
 *        (accessToken cleared) so the boot path can call /bff/auth/refresh and
 *        mint a fresh access token without bouncing the user to the popup.
 *      • Older entries are dropped.
 *  - localStorage is treated as a best-effort optimisation — quota errors,
 *    private mode, or partitioned storage degrade gracefully to in-memory.
 */
export interface OutlookBearerTokens {
  accessToken: string | null;
  refreshHandle: string | null;
  expiresAt: number | null; // epoch milliseconds
  tenantId: string | null;
}

export interface OutlookBearerState extends OutlookBearerTokens {
  setTokens: (tokens: OutlookBearerTokens) => void;
  clear: () => void;
  /** Returns access token if present and not within the 60s expiry leeway, else null. */
  getAccessToken: () => string | null;
}

const EXPIRY_LEEWAY_MS = 60_000; // refresh proactively 60s before expiry

// ────────────────────────────────────────────────────────────────────────────
// localStorage persistence
// ────────────────────────────────────────────────────────────────────────────
const STORAGE_KEY = 'outlook:bearer:v2';
const PERSISTENCE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface PersistedShape {
  accessToken: string;
  refreshHandle: string;
  expiresAt: number;
  tenantId: string | null;
  /** Wall-clock when this entry was written; used to drop entries older than PERSISTENCE_TTL_MS. */
  writtenAt: number;
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeRemove(storage: Storage | null): void {
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readFromStorage(): OutlookBearerTokens | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    // Strict shape check.
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshHandle !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      typeof parsed.writtenAt !== 'number' ||
      !(parsed.tenantId === null || typeof parsed.tenantId === 'string')
    ) {
      safeRemove(storage);
      return null;
    }

    // Drop entries older than the persistence cap regardless of expiresAt —
    // limits the worst-case window where a stolen handle is replayable.
    const age = Date.now() - parsed.writtenAt;
    if (age > PERSISTENCE_TTL_MS || age < -60_000 /* clock skew sanity */) {
      safeRemove(storage);
      return null;
    }

    const accessTokenFresh = parsed.expiresAt > Date.now() + EXPIRY_LEEWAY_MS;
    return {
      accessToken: accessTokenFresh ? parsed.accessToken : null,
      refreshHandle: parsed.refreshHandle,
      // Drop the absolute expiry once the access_token is stale so callers
      // don't reason about a bogus number.
      expiresAt: accessTokenFresh ? parsed.expiresAt : null,
      tenantId: parsed.tenantId ?? null,
    };
  } catch {
    safeRemove(storage);
    return null;
  }
}

function writeToStorage(tokens: OutlookBearerTokens): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    if (
      tokens.accessToken &&
      tokens.refreshHandle &&
      typeof tokens.expiresAt === 'number'
    ) {
      const persisted: PersistedShape = {
        accessToken: tokens.accessToken,
        refreshHandle: tokens.refreshHandle,
        expiresAt: tokens.expiresAt,
        tenantId: tokens.tenantId,
        writtenAt: Date.now(),
      };
      storage.setItem(STORAGE_KEY, JSON.stringify(persisted));
    } else {
      storage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* localStorage may throw in private mode / quota; degrades to in-memory only */
  }
}

function clearStorage(): void {
  safeRemove(getStorage());
}

// Compute initial state — hydrate if a fresh-enough cache exists. Runs ONCE
// per module load. In tests, `vi.resetModules()` re-triggers this.
const hydrated = readFromStorage();

export const useOutlookBearerStore = create<OutlookBearerState>((set, get) => ({
  accessToken: hydrated?.accessToken ?? null,
  refreshHandle: hydrated?.refreshHandle ?? null,
  expiresAt: hydrated?.expiresAt ?? null,
  tenantId: hydrated?.tenantId ?? null,

  setTokens: (tokens) => {
    set({
      accessToken: tokens.accessToken,
      refreshHandle: tokens.refreshHandle,
      expiresAt: tokens.expiresAt,
      tenantId: tokens.tenantId,
    });
    writeToStorage(tokens);
  },

  clear: () => {
    set({
      accessToken: null,
      refreshHandle: null,
      expiresAt: null,
      tenantId: null,
    });
    clearStorage();
  },

  getAccessToken: () => {
    const { accessToken, expiresAt } = get();
    if (!accessToken) return null;
    if (expiresAt !== null && Date.now() + EXPIRY_LEEWAY_MS >= expiresAt) {
      return null; // treat as expired so callers trigger refresh
    }
    return accessToken;
  },
}));
